import { useCallback, useEffect, useRef, useState } from 'react';

import { logError } from 'lib/sentry/logError';

import { AudioRecorder } from 'shared/models/AudioRecorder/AudioRecorder';
import { AudioRecorderStopResult } from 'shared/models/AudioRecorder/types';
import { getDevicesInfo } from 'shared/utils/devicesInfo';
import { isMicPermissionsDenied } from 'shared/utils/permissions';

export const useAudioRecorder = ({ isPaused }: { isPaused: boolean }) => {
  const [isAcquiringAudioTrack, setIsAcquiringAudioTrack] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>();
  const mediaRecorderRef = useRef<AudioRecorder | null>(null);

  useEffect(() => {
    if (isPaused) {
      mediaRecorderRef.current?.pause();
    }
  }, [isPaused]);

  const getLocalAudioTrack = async () => {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      throw new Error('MediaDevicesApiNotSupported');
    }

    const { hasAudioInputDevices } = await getDevicesInfo();

    if (!hasAudioInputDevices || isAcquiringAudioTrack) {
      return Promise.resolve(null);
    }

    setIsAcquiringAudioTrack(true);

    const isMicPermissionDenied = await isMicPermissionsDenied();

    if (isMicPermissionDenied) {
      throw new Error('MicrophonePermissionDenied');
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
        },
      });
      setMediaStream(mediaStream);

      return mediaStream;
    } catch (error) {
      throw error;
    } finally {
      setIsAcquiringAudioTrack(false);
    }
  };

  const startAudioRecording = useCallback(() => {
    try {
      if (mediaStream) {
        mediaRecorderRef.current = new AudioRecorder(mediaStream);
        mediaRecorderRef.current.start();
      }
    } catch (error) {
      logError(error);
    }
  }, [mediaStream]);

  const stopAudioRecording = () =>
    mediaRecorderRef.current?.stop() as AudioRecorderStopResult;

  const cancelAudioRecording = () => mediaRecorderRef.current?.cancel();

  const getRecordingInfo = () => {
    const info = mediaRecorderRef.current!.getRecordingInfo();
    mediaRecorderRef.current?.reset();
    return info;
  };

  return {
    mediaStream,
    startAudioRecording,
    stopAudioRecording,
    cancelAudioRecording,
    isAcquiringAudioTrack,
    getLocalAudioTrack,
    getRecordingInfo,
  };
};
